'use client'

/**
 * React Bits' `DriftWall`, vendored — columns of tiles drifting past each other
 * on a plane that is tilted, turned and pushed back in perspective, with the
 * pointer nudging the whole plane rather than any single tile.
 *
 * Taken from `DavidHDev/react-bits`
 * (`src/ts-tailwind/Components/DriftWall/DriftWall.tsx`) rather than installed,
 * for the same reason `WarpText` and `SpecularButton` are copied into this
 * repo: the library ships source, not a package. It needs no dependency at all
 * — it is DOM, CSS transforms and one rAF loop.
 *
 * ── WHAT CHANGED, AND WHY ────────────────────────────────────────────────
 *
 *  1. **Every tile is decoration.** The original makes each tile focusable
 *     (`tabIndex={0}`, `role="button"`) or a link, with hover/focus lift. This
 *     wall is mounted INSIDE `PillarTile`, which is itself one `<a>` — so the
 *     original would have put sixty tab stops inside a single link and given a
 *     keyboard user sixty announcements of "tile" before the next control. The
 *     wall is `aria-hidden` and the tile around it owns the affordance.
 *  2. **The per-tile hover machinery is gone with it.** It cost a
 *     `document.elementFromPoint` on every pointer move (a forced layout per
 *     frame) to decide which tile to lift — and the lift would not have been
 *     visible anyway: the tile's hover rule faded this scene to 0.35 so the
 *     "Miniaturki" pill had a quiet ground. That rule went with the bento it
 *     belonged to; this component keeps the parallax, which is the part. The plane parallax,
 *     which is what the component is actually recognisable for, is kept.
 *  3. **`next/image` instead of `<img>`.** The wall that stood here already
 *     used it, and sixty raw `<img>` tags pointed at 1280px JPEGs is not a
 *     trade this page can make for a decorative background.
 *  4. **The frame loop stops when nobody is looking.** The original runs its
 *     rAF forever — off screen, in a background tab, and under
 *     `prefers-reduced-motion` (where it keeps looping to re-apply a transform
 *     that never changes). Here an `IntersectionObserver`, `visibilitychange`
 *     and the motion query all cancel it, and the static layout is written
 *     once on the way out so the wall still LOOKS right while frozen.
 *  5. **`overlayColor` defaults onto the brand arc.** Upstream's `#060010` is
 *     a near-black blue-violet; the site's decorative colour is the FLOU
 *     gradient's own arc (`lib/brand-tones.ts`, hue 285–352) and `oklch(0.12
 *     0.03 300)` is that arc at scrim strength.
 *  6. **`useEffect`, not `useLayoutEffect`,** for the resize observer. This
 *     file is a client component inside a server-rendered page, and
 *     `useLayoutEffect` warns on every render pass on the server.
 *  7. **A named export**, because this repo does not use default exports, and
 *     `cn` in place of the library's private `cx`.
 *  8. **No `pauseOnHover` / `lift` / `edgeBlur` props.** They only drove the
 *     per-tile interaction that item 1 removed; a prop that cannot do anything
 *     is worse than no prop.
 *  9. **The loop rule is off by one copy upstream, and fixed here.** A
 *     centred plane needs `copyHeight × (copies − 2) ≥ window`, not the
 *     `− 1` that is right for a top-anchored track. Upstream's formula returns
 *     2 copies for a tall column, which satisfies neither, so the column
 *     empties from the bottom once per cycle. The full derivation is on
 *     `columnMeta` below.
 * 10. **`overlayOpacity` is a prop**, because it multiplies with `dim` and the
 *     pair decides how visible the pictures actually are.
 *
 * Everything else — the golden-ratio column speeds, the alternating
 * directions, the modulo wrap itself, the elliptical mask — is as published.
 */

import Image from 'next/image'
import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

export type DriftWallItem = {
  src: string
  /** Decorative by default: the wall is `aria-hidden` inside its own tile. */
  alt?: string
}

export interface DriftWallProps {
  items: DriftWallItem[]
  columns?: number
  tileWidth?: number
  tileHeight?: number
  gap?: number
  radius?: number
  /** Degrees of `rotateX` on the plane. */
  tilt?: number
  /** Degrees of `rotateY`. */
  turn?: number
  /** Degrees of `rotateZ`. */
  roll?: number
  perspective?: number
  /** How far back the plane sits, in px. */
  depth?: number
  /** Px per second, before the per-column factor. */
  speed?: number
  direction?: 'up' | 'down'
  /** How much the columns disagree about speed. 0 = one moving block. */
  variance?: number
  /** How far the pointer tips the plane. 0 disables the pointer entirely. */
  parallax?: number
  /** How much of the edge the mask eats, 0–1. */
  fade?: number
  /** Resting opacity of a tile. */
  dim?: number
  grayscale?: boolean
  overlayColor?: string
  /**
   * How strongly the scrim sits over each picture, 0–1.
   *
   * FLOU: a prop, where upstream hardcodes `0.42`. It has to be reachable
   * because `dim` and this multiply — 0.62 × 0.58 left the work samples at
   * about a third of their real brightness, which is the wrong end of the
   * trade for the one tile on this page whose whole argument is the pictures.
   */
  overlayOpacity?: number
  /** Passed straight to `next/image`. */
  sizes?: string
  className?: string
  style?: CSSProperties
}

interface ColumnMeta {
  copyHeight: number
  /** The column's items, already laid end to end as many times as it takes. */
  tiles: Array<{ id: string; src: string }>
}

/**
 * A stable per-column speed multiplier.
 *
 * The golden ratio is doing real work: stepping by 0.618 and taking the
 * fractional part visits the unit interval without ever landing twice in the
 * same neighbourhood, so five columns get five genuinely different speeds from
 * one line of arithmetic and no random seed. A seed would have re-rolled on
 * every render and made the wall jump.
 */
const columnFactor = (index: number, variance: number): number => {
  const pseudo = ((index * 0.6180339887 + 0.35) % 1) * 2 - 1
  return 1 + variance * pseudo
}

const MASK =
  'radial-gradient(ellipse 78% 82% at 50% 46%, #000 var(--dw-edge), transparent 100%), ' +
  'linear-gradient(to top, #000 var(--dw-edge), transparent 100%)'

export function DriftWall({
  items,
  columns = 5,
  tileWidth = 208,
  tileHeight = 117,
  gap = 14,
  radius = 10,
  tilt = 16,
  turn = -14,
  roll = 0,
  perspective = 1200,
  depth = 120,
  speed = 34,
  direction = 'up',
  variance = 0.45,
  parallax = 0.6,
  fade = 0.6,
  dim = 0.62,
  grayscale = false,
  // FLOU: the brand arc's near-black, not upstream's `#060010`. See note 5.
  overlayColor = 'oklch(0.12 0.03 300)',
  overlayOpacity = 0.38,
  sizes = '208px',
  className,
  style,
}: DriftWallProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const planeRef = useRef<HTMLDivElement>(null)
  const trackRefs = useRef<(HTMLDivElement | null)[]>([])

  const offsetsRef = useRef<number[]>([])
  const velocitiesRef = useRef<number[]>([])
  const pointerRef = useRef({ x: 0, y: 0 })
  const pointerDampedRef = useRef({ x: 0, y: 0 })

  const [containerHeight, setContainerHeight] = useState(480)
  const [reduced, setReduced] = useState(false)

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(query.matches)
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  /**
   * The columns PARTITION the list — every `columns`-th item — so a picture
   * belongs to exactly one of them and cannot appear twice on screen. This is
   * the same call the marquee wall this replaced made, and for the same
   * reason: the gallery is sorted by dominant colour, so a contiguous slice is
   * four near-identical fire shots reading as one image repeated.
   */
  const columnItems = useMemo<DriftWallItem[][]>(() => {
    const lanes: DriftWallItem[][] = Array.from({ length: columns }, () => [])
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (item) lanes[i % columns]?.push(item)
    }
    return lanes.map((lane) => (lane.length ? lane : items.slice(0, 1)))
  }, [items, columns])

  /**
   * How tall one copy of a column is, and the column laid end to end as many
   * times as it takes to LOOP without a seam.
   *
   * ── THE SEAMLESSNESS RULE, AND WHY UPSTREAM'S IS OFF BY ONE COPY ─────────
   *
   * A column travels exactly one copy and wraps: `offset` runs 0 → copyHeight
   * and then starts again, which is invisible only while the visible window is
   * still full of tiles at the far end of that travel.
   *
   * The plane is CENTRED on the container (`top-1/2 left-1/2` and a −50%
   * translate), so a track of `copies × copyHeight` spans ±half of that about
   * the container's middle. Travel one copy up and the track's bottom edge
   * sits at `(copies × copyHeight) / 2 − copyHeight`, and that has to still
   * reach the bottom of the window:
   *
   *     copyHeight × (copies − 2) ≥ window
   *
   * Upstream adds ONE spare copy (`ceil(window / copyHeight) + 1`), which is
   * the rule for a track anchored at the top of its box — the shape the old
   * `.flou-vtrack` marquee in this repo had, and where `(copies − 1) × block ≥
   * box` is right. Centred, half the spare is above the window where nothing
   * can see it, so one copy is not enough and the column empties from the
   * bottom once a cycle. With six tiles a column and a 480px tile the formula
   * returns exactly 2, which is the worst case: `copyHeight × 0 ≥ window` is
   * false for every window, so it seams at EVERY size rather than at some.
   *
   * `+ 2`, and a floor of 3 copies, is that rule. The owner's word for the
   * symptom was *„być w pętli"* — he had watched it restart.
   *
   * `× 1.6` on the window is the plane's own doing: it is over-scaled 1.18,
   * pushed 120px back and tilted 16°, so one screen pixel of the container is
   * more than one pixel of plane, and the tilt makes the far half of the plane
   * cover more ground still.
   *
   * FLOU: the repeated list is also BUILT here, with an id per element, rather
   * than rendered from a bare `Array.from({ length: copies })` index as
   * upstream does. A key made of two loop indices is a key made of positions,
   * which is the one thing a key may not be.
   */
  const columnMeta = useMemo<ColumnMeta[]>(() => {
    const unit = tileHeight + gap
    return columnItems.map((lane, c) => {
      const copyHeight = Math.max(unit, lane.length * unit)
      const copies = Math.max(3, Math.ceil((containerHeight * 1.6) / copyHeight) + 2)
      const tiles = Array.from({ length: copies * lane.length }, (_, i) => {
        const item = lane[i % lane.length]
        return { id: `c${c}-${i}`, src: item?.src ?? '' }
      })
      return { copyHeight, tiles }
    })
  }, [columnItems, tileHeight, gap, containerHeight])

  const baseVelocities = useMemo<number[]>(() => {
    const dirSign = direction === 'up' ? 1 : -1
    return columnItems.map((_, c) => {
      const altSign = c % 2 === 0 ? 1 : -1
      return speed * columnFactor(c, variance) * dirSign * altSign
    })
  }, [columnItems, speed, direction, variance])

  useEffect(() => {
    // Each column starts at a different phase, so the five do not line up into
    // one band on the first frame.
    offsetsRef.current = columnMeta.map((meta, c) => meta.copyHeight * ((c * 0.37) % 1))
    velocitiesRef.current = columnItems.map(() => 0)
  }, [columnMeta, columnItems])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    // FLOU: `useEffect`, not `useLayoutEffect` — see note 6 at the top.
    const observer = new ResizeObserver(([entry]) => {
      setContainerHeight(entry?.contentRect.height || 480)
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let raf = 0
    let lastTs: number | null = null
    let visible = true
    let pageVisible = !document.hidden

    const applyPlane = (px: number, py: number) => {
      const plane = planeRef.current
      if (!plane) return
      plane.style.transform =
        `translate(-50%, -50%) scale(1.18) ` +
        `rotateX(${tilt + py}deg) rotateY(${turn + px}deg) rotateZ(${roll}deg) ` +
        `translateZ(${-depth}px)`
    }

    const applyTracks = () => {
      for (let c = 0; c < trackRefs.current.length; c++) {
        const el = trackRefs.current[c]
        if (el) el.style.transform = `translate3d(0, ${-(offsetsRef.current[c] ?? 0)}px, 0)`
      }
    }

    // The frozen pose. Written once whenever the loop stops, so a wall that is
    // off screen, in a hidden tab or under reduced motion still LOOKS like the
    // wall rather than collapsing into an unrotated grid.
    const freeze = () => {
      applyPlane(pointerDampedRef.current.x, pointerDampedRef.current.y)
      applyTracks()
    }

    const frame = (ts: number) => {
      if (lastTs === null) lastTs = ts
      const dt = Math.min(0.05, Math.max(0, ts - lastTs) / 1000)
      lastTs = ts

      const maxTilt = parallax * 8
      const damp = 1 - Math.exp(-dt / 0.12)
      pointerDampedRef.current.x +=
        (pointerRef.current.x * maxTilt - pointerDampedRef.current.x) * damp
      pointerDampedRef.current.y +=
        (-pointerRef.current.y * maxTilt - pointerDampedRef.current.y) * damp
      applyPlane(pointerDampedRef.current.x, pointerDampedRef.current.y)

      for (let c = 0; c < trackRefs.current.length; c++) {
        const meta = columnMeta[c]
        if (!meta) continue
        const target = baseVelocities[c] ?? 0
        const ease = 1 - Math.exp(-dt / 0.28)
        const velocity =
          (velocitiesRef.current[c] ?? 0) + (target - (velocitiesRef.current[c] ?? 0)) * ease
        velocitiesRef.current[c] = velocity
        const next =
          ((((offsetsRef.current[c] ?? 0) + velocity * dt) % meta.copyHeight) + meta.copyHeight) %
          meta.copyHeight
        offsetsRef.current[c] = next
        const el = trackRefs.current[c]
        if (el) el.style.transform = `translate3d(0, ${-next}px, 0)`
      }

      raf = requestAnimationFrame(frame)
    }

    const sync = () => {
      const shouldRun = visible && pageVisible && !reduced
      if (shouldRun && !raf) {
        lastTs = null
        raf = requestAnimationFrame(frame)
        return
      }
      if (!shouldRun && raf) {
        cancelAnimationFrame(raf)
        raf = 0
      }
      if (!shouldRun) freeze()
    }

    const intersection = new IntersectionObserver(
      ([entry]) => {
        visible = entry?.isIntersecting ?? true
        sync()
      },
      { threshold: 0 },
    )
    intersection.observe(container)

    const onVisibility = () => {
      pageVisible = !document.hidden
      sync()
    }
    document.addEventListener('visibilitychange', onVisibility)

    const onPointerMove = (event: PointerEvent) => {
      if (parallax <= 0 || reduced) return
      const rect = container.getBoundingClientRect()
      if (rect.width <= 0 || rect.height <= 0) return
      pointerRef.current = {
        x: (event.clientX - rect.left) / rect.width - 0.5,
        y: (event.clientY - rect.top) / rect.height - 0.5,
      }
    }
    const onPointerLeave = () => {
      pointerRef.current = { x: 0, y: 0 }
    }
    container.addEventListener('pointermove', onPointerMove)
    container.addEventListener('pointerleave', onPointerLeave)

    freeze()
    sync()

    return () => {
      if (raf) cancelAnimationFrame(raf)
      intersection.disconnect()
      document.removeEventListener('visibilitychange', onVisibility)
      container.removeEventListener('pointermove', onPointerMove)
      container.removeEventListener('pointerleave', onPointerLeave)
    }
  }, [baseVelocities, columnMeta, parallax, reduced, tilt, turn, roll, depth])

  const cssVars = useMemo<CSSProperties>(
    () =>
      ({
        '--dw-tile-w': `${tileWidth}px`,
        '--dw-tile-h': `${tileHeight}px`,
        '--dw-gap': `${gap}px`,
        '--dw-radius': `${radius}px`,
        '--dw-dim': dim,
        '--dw-scrim': overlayOpacity,
        '--dw-gray': grayscale ? 1 : 0,
        '--dw-overlay': overlayColor,
        '--dw-edge': `${Math.max(0, (1 - fade) * 100)}%`,
        perspective: `${perspective}px`,
        perspectiveOrigin: '50% 50%',
        WebkitMaskImage: MASK,
        maskImage: MASK,
        WebkitMaskComposite: 'source-in',
        maskComposite: 'intersect',
        ...style,
      }) as CSSProperties,
    [
      tileWidth,
      tileHeight,
      gap,
      radius,
      dim,
      grayscale,
      overlayColor,
      overlayOpacity,
      fade,
      perspective,
      style,
    ],
  )

  return (
    <div
      ref={containerRef}
      // FLOU: `pointer-events-auto` re-enables the pointer inside
      // `PillarTile`'s `pointer-events-none` scene wrapper, so the plane can
      // still follow the cursor. The tiles themselves stay inert, so a click
      // anywhere on the wall reaches the `<a>` this sits in.
      className={cn('pointer-events-auto relative h-full w-full overflow-hidden', className)}
      style={cssVars}
      aria-hidden="true"
    >
      <div
        ref={planeRef}
        className="absolute top-1/2 left-1/2 flex flex-row [transform-origin:50%_50%] [transform-style:preserve-3d] will-change-transform"
      >
        {columnMeta.map((meta, c) => (
          <div
            key={meta.tiles[0]?.id ?? c}
            className="relative w-[calc(var(--dw-tile-w)+var(--dw-gap))] [transform-style:preserve-3d]"
          >
            <div
              className="flex flex-col [transform-style:preserve-3d] will-change-transform"
              ref={(el) => {
                trackRefs.current[c] = el
              }}
            >
              {meta.tiles.map((tile) => (
                <span
                  key={tile.id}
                  className="relative block h-[calc(var(--dw-tile-h)+var(--dw-gap))] w-full flex-none"
                >
                  <span className="absolute inset-[calc(var(--dw-gap)/2)] block overflow-hidden rounded-[var(--dw-radius)] bg-black/40 opacity-[var(--dw-dim)]">
                    <Image
                      src={tile.src}
                      alt=""
                      fill
                      sizes={sizes}
                      draggable={false}
                      className="object-cover select-none [filter:grayscale(var(--dw-gray))_saturate(0.92)]"
                    />
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 bg-[var(--dw-overlay)] opacity-[var(--dw-scrim)]"
                    />
                  </span>
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
